import React, { createContext, useState, useContext, useCallback } from 'react';
import audioService from '../services/audioService';
import notificationService from '../services/notificationService';

const NotificationContext = createContext();

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [persistentNotifications, setPersistentNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const addNotification = (message, type = 'success', duration = 3000, playSound = false) => {
    console.log('🔔 NotificationContext: addNotification called', { message, type, duration, playSound });

    const id = Date.now() + Math.random();
    const notification = {
      id,
      message,
      type,
      duration,
      timestamp: Date.now()
    };

    setNotifications(prev => [...prev, notification]);

    // Play notification sound if requested
    if (playSound) {
      console.log('🔔 NotificationContext: Playing notification sound...');
      audioService.playNotificationSound();
    } else {
      console.log('🔔 NotificationContext: Not playing sound (playSound: false)');
    }

    // Auto-remove notification after duration
    if (duration > 0) {
      setTimeout(() => {
        removeNotification(id);
      }, duration);
    }

    return id;
  };

  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  };

  const clearAllNotifications = () => {
    setNotifications([]);
  };

  const addOrderNotification = (message, type = 'info', duration = 5000) => {
    console.log('🔔 NotificationContext: addOrderNotification called', { message, type, duration });
    return addNotification(message, type, duration, true);
  };

  // Handle WebSocket notifications
  const handleWebSocketNotification = useCallback((wsNotification) => {
    console.log('🔔 NotificationContext: handleWebSocketNotification called', {
      type: wsNotification.notification_type,
      title: wsNotification.title
    });

    // Add to toast notifications with sound
    addNotification(
      wsNotification.message || wsNotification.title,
      'info',
      5000,
      true  // Play sound for all admin notifications
    );

    // Add to persistent notifications list
    const formattedNotification = notificationService.formatNotification({
      id: wsNotification.id || Date.now(),
      title: wsNotification.title,
      message: wsNotification.message,
      notification_type: wsNotification.notification_type || 'system_notification',
      is_read: false,
      created_at: new Date().toISOString(),
      time_ago: 'Just now'
    });

    setPersistentNotifications(prev => [formattedNotification, ...prev]);
    setUnreadCount(prev => prev + 1);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Mark notification as read
  const markNotificationAsRead = useCallback(async (notificationId) => {
    try {
      await notificationService.markAsRead(notificationId);

      // Update local state
      setPersistentNotifications(prev =>
        prev.map(notification =>
          notification.id === notificationId
            ? { ...notification, is_read: true }
            : notification
        )
      );

      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  }, []);

  // Update unread count
  const updateUnreadCount = useCallback((count) => {
    setUnreadCount(count);
  }, []);

  // Refresh persistent notifications
  const refreshPersistentNotifications = useCallback(async () => {
    try {
      const response = await notificationService.getNotifications({ limit: 20 });
      const formattedNotifications = notificationService.formatNotifications(
        response.notifications
      );
      setPersistentNotifications(formattedNotifications);
      setUnreadCount(response.unread_count);
    } catch (error) {
      console.error('Error refreshing notifications:', error);
    }
  }, []);

  const initializeAudio = async () => {
    console.log('🔔 NotificationContext: Initializing audio...');
    try {
      await audioService.initialize();
      console.log('🔔 NotificationContext: Audio initialized successfully');
    } catch (error) {
      console.error('🔊 Audio initialization failed:', error);
    }
  };

  const value = {
    // Toast notifications (temporary)
    notifications,
    addNotification,
    addOrderNotification,
    removeNotification,
    clearAllNotifications,

    // Persistent notifications (dropdown)
    persistentNotifications,
    unreadCount,
    handleWebSocketNotification,
    markNotificationAsRead,
    updateUnreadCount,
    refreshPersistentNotifications,

    // Audio
    initializeAudio
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
